import styled from "styled-components";
import { breakpoint, theme } from "@utilities";
import { IPointsData, IClaimDetail, IPointsHistoryItem } from "@interfaces";
import moment from "moment";
import CopyButton from "src/Module/CopyButton";
import { useNavigate } from "react-router-dom";
import { PATH_COMMISSION_AGENT, PATH_POINTS_LEARN_MORE } from "@routes";
import { selectAccount, selectPoints, useAppSelector, useAppDispatch } from "@redux";
import { fetchPoints } from "@redux";
import { useEffect, useState } from "react";
import _ from "lodash";
import { formatMaxOneDecimal } from "src/Module/Checkout/pointsHelpers";

export const PointsTab = ({ onShowAllChange }: { onShowAllChange?: (show: boolean) => void }) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { account, metadata } = useAppSelector(selectAccount);
    const { data: pointsData, history: PointsHistoryData } = useAppSelector(selectPoints);
    const [showAll, setShowAll] = useState(false);

    const handleToggleShowAll = () => {
        const newShowAll = !showAll;
        setShowAll(newShowAll);
        onShowAllChange?.(newShowAll);
    };
    const displayPointsData: IPointsData = pointsData || {
        subSystemType: "",
        pointsBalance: 0,
        availableBalance: 0,
        pointsMultiplier: 0,
    };

    useEffect(() => {
        if (!account?.is_agent_integrated) return;
        dispatch(fetchPoints());
    }, [account?.is_agent_integrated, dispatch]);

    const todayEndOfDay = moment().endOf("day").valueOf();
    const safeHistoryData = PointsHistoryData || [];
    const toMillis = (d?: string | null) => (d ? moment(d).valueOf() : null);

    const filteredHistoryData = _.chain(safeHistoryData)
        .map((item) => {
            const itemAvailableVal = toMillis((item as IPointsHistoryItem).availableDate);
            const filteredClaims =
                itemAvailableVal !== null && itemAvailableVal > todayEndOfDay
                    ? []
                    : _.filter(item.claimDetails ?? [], (c) => {
                          const claimedVal = toMillis((c as IClaimDetail).claimedDate);
                          return claimedVal !== null && claimedVal <= todayEndOfDay;
                      });
            return { ...(item as IPointsHistoryItem), claimDetails: filteredClaims };
        })
        .filter((item) => {
            const itemAvailableVal = toMillis((item as IPointsHistoryItem).availableDate);
            const hasAvailable = itemAvailableVal !== null && itemAvailableVal <= todayEndOfDay;
            const hasAnyValidClaims = (item.claimDetails?.length ?? 0) > 0;
            return hasAvailable || hasAnyValidClaims;
        })
        .value();

    return (
        <PointsView>
            {!showAll && (
                <EarningsBalanceSection>
                    <SectionHeader>
                        <h2>Your current balance</h2>
                        <HelpIcon>?</HelpIcon>
                    </SectionHeader>
                    <EarningsBalanceCards>
                        <EarningsBalanceCard $type="total">
                            <p>Total points</p>
                            <h3>{displayPointsData.pointsBalance}</h3>
                        </EarningsBalanceCard>
                        <EarningsBalanceCard $type="available">
                            <p>Available points</p>
                            <h3>{displayPointsData.availableBalance}</h3>
                        </EarningsBalanceCard>
                        <EarningsBalanceCard $type="pending">
                            <p>Pending points</p>
                            {/* Same formatter the checkout uses for points, so a
                                whole number shows as "0" rather than "0.00". */}
                            <h3>
                                {formatMaxOneDecimal(
                                    displayPointsData.pointsBalance -
                                        displayPointsData.availableBalance,
                                )}
                            </h3>
                        </EarningsBalanceCard>
                    </EarningsBalanceCards>
                </EarningsBalanceSection>
            )}
            <SectionHeader $showAll={showAll}>
                <h2>Points History</h2>
                <ViewAllLink onClick={handleToggleShowAll}>
                    {showAll ? "Show Less" : "View All"}
                </ViewAllLink>
            </SectionHeader>
            <HistorySection>
                {(showAll ? filteredHistoryData : filteredHistoryData.slice(0, 4)).length === 0 ? (
                    <EmptyState>
                        <p>You don't have any History</p>
                    </EmptyState>
                ) : (
                    <TransactionList>
                        {(showAll ? filteredHistoryData : filteredHistoryData.slice(0, 4)).map(
                            (item, index) => (
                                <TransactionItem key={index}>
                                    <TransactionLeft>
                                        <TransactionId>
                                            {item.bookingReference || "Points Transaction"}
                                            <CopyButton value={item.bookingReference} />
                                        </TransactionId>
                                        <TransactionDate>
                                            {moment(item.availableDate).format("MMM DD, YYYY")}
                                        </TransactionDate>
                                        <ViewDetailsLink>View Details</ViewDetailsLink>
                                    </TransactionLeft>
                                    <TransactionRight>
                                        <TransactionAmount $positive={(item.amount || 0) > 0}>
                                            {(item.amount || 0) > 0 ? "+" : ""}
                                            {item.amount || 0}
                                        </TransactionAmount>
                                    </TransactionRight>
                                </TransactionItem>
                            ),
                        )}
                    </TransactionList>
                )}
            </HistorySection>

            {!showAll && (
                <>
                    <InfoCard onClick={() => navigate(PATH_POINTS_LEARN_MORE)}>
                        <img src="/images/Gift.svg" alt="gift" />
                        <InfoContent>
                            <h4>How Points work?</h4>
                            <p>Points rule, redemption, FAQs and terms</p>
                        </InfoContent>
                        <img src="/images/ChevronVectorRight.svg" alt="chevron" />
                    </InfoCard>

                    {metadata?.is_upgrade_to_commission_enabled && (
                        <InfoCard onClick={() => navigate(PATH_COMMISSION_AGENT)}>
                            <img src="/images/Currency.svg" alt="currency" />
                            <InfoContent>
                                <h4>Upgrade to Commission</h4>
                                <p> Upgrade and earn money from every trips you book.</p>
                            </InfoContent>
                            <img src="/images/ChevronVectorRight.svg" alt="chevron" />
                        </InfoCard>
                    )}
                </>
            )}
        </PointsView>
    );
};

const PointsView = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

const EarningsBalanceSection = styled.div`
    background: white;
    padding: 24px;
    border-radius: 0px 0px 12px 12px;
`;

const SectionHeader = styled.div<{ $showAll?: boolean }>`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: ${({ $showAll }) => ($showAll ? "24px" : "0")};

    h2 {
        font-size: 18px;
        font-weight: 600;
        margin: 0;
    }
`;

const HelpIcon = styled.div`
    width: 24px;
    height: 24px;
    border-radius: 50%;
    border: 2px solid ${theme.colors.main};
    color: ${theme.colors.main};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
`;

const EarningsBalanceCards = styled.div`
    margin-top: 24px;
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 16px;

    ${breakpoint.breakOnlyMobile`
        grid-template-columns: 1fr 1fr 1fr;
        gap: 8px;
    `}
`;

/**
 * One hue per card, each as a tinted panel with a matching border and figure.
 * The tints are the same colour at low opacity rather than separate hex values,
 * so a palette change carries through instead of leaving three fixed pastels
 * behind.
 */
const cardColors = {
    total: { bg: `${theme.colors.main}26`, border: theme.colors.main, text: theme.colors.main },
    available: { bg: `${theme.colors.red}14`, border: theme.colors.red, text: theme.colors.red },
    pending: { bg: "#F2F2F2", border: "#BDBDBD", text: "#747474" },
};

const EarningsBalanceCard = styled.div<{ $type: "total" | "available" | "pending" }>`
    background: ${({ $type }) => cardColors[$type].bg};
    border-radius: 12px;
    padding: 24px;
    text-align: center;
    border: 1px solid ${({ $type }) => cardColors[$type].border};

    p {
        font-size: 14px;
        color: #131214;
        margin: 0 0 12px 0;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 4px;

        span {
            font-size: 18px;
        }
    }

    h3 {
        font-size: 32px;
        font-weight: 700;
        margin: 0;
        color: ${({ $type }) => cardColors[$type].text};
    }

    ${breakpoint.breakOnlyMobile`
        padding: 12px 8px;
        height: 76px;
        display: flex;
        flex-direction: column;
        justify-content: center;

        p {
            font-size: 11px;
            margin: 0 0 4px 0;

            span {
                font-size: 14px;
            }
        }

        h3 {
            font-size: 20px;
            font-weight: 600;
        }
    `}
`;

const HistorySection = styled.div`
    background: white;
    border-radius: 12px;
    padding: 24px;
`;

const ViewAllLink = styled.a`
    font-size: 14px;
    /* The same one-off grey used by the breadcrumb and location picker. */
    color: #747474;
    cursor: pointer;
    text-decoration: none;

    &:hover {
        text-decoration: underline;
    }
`;

const TransactionList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const TransactionItem = styled.div`
    display: flex;
    justify-content: space-between;
    padding: 16px;
    border-radius: 8px;
    border: 1px solid #e6e9eb;
    background: white;
`;

const TransactionLeft = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const TransactionId = styled.div`
    font-size: 14px;
    font-weight: 600;
    color: #131214;
    display: flex;
    align-items: center;
    gap: 8px;
`;

const TransactionDate = styled.div`
    font-size: 12px;
    color: #6e7375;
`;

const ViewDetailsLink = styled.a`
    font-size: 12px;
    color: ${theme.colors.highlight};
    cursor: pointer;
    text-decoration: underline;
`;

const TransactionRight = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 8px;
`;

const TransactionAmount = styled.div<{ $positive?: boolean }>`
    font-size: 16px;
    font-weight: 600;
    color: ${({ $positive }) => ($positive ? "#00BFA5" : "#131214")};
`;

const InfoCard = styled.div`
    background: white;
    border-radius: 12px;
    padding: 20px;
    display: flex;
    align-items: center;
    gap: 16px;
    cursor: pointer;
    transition: box-shadow 0.2s ease;

    &:hover {
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }
`;

const InfoContent = styled.div`
    flex: 1;

    h4 {
        font-size: 16px;
        font-weight: 600;
        margin: 0 0 4px 0;
        color: #131214;
    }

    p {
        font-size: 13px;
        color: #6e7375;
        margin: 0;
    }
`;

const EmptyState = styled.div`
    text-align: center;
    padding: 20px;
    color: #6e7375;
    font-size: 14px;
`;
